import { get, post } from './api'
import { Capacitor } from '@capacitor/core'
import { nativePushState, enableNativePush } from './nativePush'

/**
 * Push subscription, client side.
 *
 * The push itself carries no payload — it is a wake-up, and the service worker
 * then asks the API what to show. See services/api/src/push.ts for why.
 */

/** Stable per-install id, so a re-subscribe with a fresh endpoint merges rather than duplicating. */
function deviceId(): string {
  let id = localStorage.getItem('deviceId')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('deviceId', id)
  }
  return id
}

export type PushState = 'unsupported' | 'blocked' | 'off' | 'on'

export async function pushState(): Promise<PushState> {
  if (Capacitor.getPlatform() === 'ios') return nativePushState()
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  return sub ? 'on' : 'off'
}

/**
 * Ask for permission and subscribe.
 *
 * Must be called from a user gesture: Safari refuses the permission prompt
 * otherwise, and refuses it permanently for that origin.
 */
export async function enablePush(label?: string): Promise<PushState> {
  if (Capacitor.getPlatform() === 'ios') return enableNativePush(label)
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off'

  const { key } = await get<{ key: string | null }>('/api/push/key')
  if (!key) throw new Error('This deployment has no VAPID public key configured.')

  const reg = await navigator.serviceWorker.ready
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }))

  const json = sub.toJSON()
  const { subscriptionId } = await post<{ subscriptionId: string }>('/api/push/subscribe', {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    platform: 'web',
    deviceId: deviceId(),
    label: label ?? navigator.userAgent.slice(0, 60),
  })

  // The service worker needs this to ask what to show after it is woken.
  localStorage.setItem('subscriptionId', subscriptionId)
  return 'on'
}
